import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import type { UIMessage } from 'ai';
import { AIChatMessageContent } from './AIChatMessageContent';
import { AIAssistantSystemPromptToggle } from './AIAssistantSystemPromptToggle';
import { CopyMessageButton, extractMessageText } from './CopyMessageButton';
import { ToolPartToggle } from './ToolPartToggle';

type Props = {
  message: UIMessage;
  debugEnabled: boolean;
};

// 確定済みメッセージはストリーミング中も参照が保たれるため、memo で
// ReactMarkdown の再パースを生成中の末尾メッセージだけに抑えられる。
export const AIChatMessageRow = memo(function AIChatMessageRow({ message, debugEnabled }: Props) {
  const { t } = useTranslation();

  const debug = debugEnabled ? (
    <ToolPartToggle label={t('viewModel.aiChat.debugInfo')}>
      <pre>{JSON.stringify(message, null, '  ')}</pre>
    </ToolPartToggle>
  ) : null;

  switch (message.role) {
    case 'system':
      return (
        <div className="whitespace-pre-line text-wrap text-xs text-gray-500">
          {debug}
          {/* @ts-expect-error システムプロンプトは型定義あってないので追加 */}
          <AIAssistantSystemPromptToggle>{message.content}</AIAssistantSystemPromptToggle>
        </div>
      );
    case 'user':
      return (
        <div className="flex flex-col items-start">
          <div className="whitespace-pre-line text-wrap rounded-lg bg-zinc-100 px-2 py-1 text-sm text-zinc-700">
            {debug}
            <AIChatMessageContent message={message} />
          </div>
        </div>
      );
    case 'assistant':
      return (
        <div className="group text-sm text-gray-800">
          {debug}
          <div className="float-right px-0.5 pb-0.5 opacity-0 transition-opacity group-hover:opacity-100">
            <CopyMessageButton text={extractMessageText(message.parts)} />
          </div>
          <AIChatMessageContent message={message} />
        </div>
      );
    default:
      return debug;
  }
});
